
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  Field,
  Heading,
  Input,
  Select,
  Stack,
  Text,
} from '@client/shared/design';
import { messageFor } from '@client/shared/lib/error-message.ts';
import { useCurrentUser, SESSION_QUERY_KEY } from '@client/shared/auth/index.ts';
import { fetchGroupMembers, transferGroupOwnership } from './group-operations.ts';
import styles from './GroupSettings.module.css';

export function TransferOwnershipSection() {
  const session = useCurrentUser();
  const queryClient = useQueryClient();
  const [toUserId, setToUserId] = useState('');
  const [confirmName, setConfirmName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const members = useQuery({
    queryKey: ['group-members'],
    queryFn: fetchGroupMembers,
  });

  const mutation = useMutation({
    mutationFn: transferGroupOwnership,
    // Roles changed for both sides; the refetched session drops the owner-only UI.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY }),
    onError: (err) => setError(messageFor(err, 'Couldn’t transfer ownership. Try again.')),
  });

  const eligible = (members.data ?? []).filter(
    (m) => m.isActive && !m.pending && m.id !== session?.user.id,
  );
  const selected = eligible.find((m) => m.id === toUserId) ?? null;
  const confirmed = selected != null && confirmName.trim() === selected.name;

  return (
    <Card pad="lg" as="section" aria-labelledby="transfer-ownership-heading" className={styles.dangerCard}>
      <Stack gap={5}>
        <Stack gap={1}>
          <Heading level={3} as="h2" id="transfer-ownership-heading" className={styles.dangerTitle}>
            Transfer ownership
          </Heading>
          <Text size="sm" tone="soft">
            Hand this group to another member. You’ll keep admin access, but only the new owner
            can transfer it back. <strong>This is permanent.</strong>
          </Text>
        </Stack>

        {eligible.length === 0 ? (
          <Text size="sm" tone="soft">
            {members.isPending
              ? 'Loading members…'
              : 'No other active members to transfer to. Invite someone first.'}
          </Text>
        ) : (
          <Stack gap={4}>
            <Field label="New owner">
              {(wiring) => (
                <Select
                  id={wiring.id}
                  value={toUserId}
                  onChange={(e) => {
                    setToUserId(e.target.value);
                    setConfirmName('');
                    setError(null);
                  }}
                >
                  <option value="">Choose a member…</option>
                  {eligible.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name} ({m.email})
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            {selected && (
              <Field
                label={`Type “${selected.name}” to confirm`}
                hint={`${selected.name} becomes the owner; you become an admin.`}
              >
                {(wiring) => (
                  <Input
                    id={wiring.id}
                    value={confirmName}
                    onChange={(e) => setConfirmName(e.target.value)}
                    autoComplete="off"
                  />
                )}
              </Field>
            )}

            {error && (
              <Text size="sm" tone="danger" role="alert">
                {error}
              </Text>
            )}

            <div>
              <Button
                tone="danger"
                disabled={!confirmed}
                loading={mutation.isPending}
                onClick={() => {
                  setError(null);
                  mutation.mutate({ toUserId });
                }}
              >
                Transfer ownership
              </Button>
            </div>
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
